import {
  useState,
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useCallback,
} from "react";
import ReactPlayer from "react-player";
import { AspectRatio } from "@chakra-ui/layout";
import { callWithAuth, postListenTime } from "@/api/Api";
import { PostListenTimeRequest } from "@shared_types";
import "../../App.css";
import { useUpgrade } from "../../context/UpgradeContext";
import { IoMdPlay } from "react-icons/io";
// Cloudflare Stream HLS URL — shared helper (single source of truth for the
// customer subdomain; mirrors iOS CloudflareVideoPlayer).
import { cloudflareHlsUrl, cloudflareFileConfig } from "@/lib/cloudflare";

interface ActivityPlayerProps {
  youtubeId: string;
  /** Cloudflare Stream id — YouTube↔Cloudflare fallback source (iOS parity). */
  cloudflareId?: string;
  /** Preferred player from the backend (`'unset' | 'cloudflare' | 'youtube'`). */
  player?: "unset" | "cloudflare" | "youtube";
  onTimeUpdate: (time: number) => void;
  startTime: number;
  endTime: number;
  onPlay: () => void;
  onTrackListeningTime?: (time: number) => void | undefined;
}

type Source = "youtube" | "cloudflare";

export interface ActivityPlayerHandle {
  seekTo: (time: number) => void;
  pause: () => void;
  play: () => void;
  triggerOverlayClick: () => void;
}

const ActivityPlayer = forwardRef<ActivityPlayerHandle, ActivityPlayerProps>(
  (
    {
      youtubeId,
      cloudflareId,
      player,
      onTrackListeningTime,
      onTimeUpdate,
      startTime,
      endTime,
      onPlay,
    },
    ref
  ) => {
    const { isUpgrading } = useUpgrade();

    // Choose the initial source: backend preference, else YouTube if present,
    // else Cloudflare. We fall back to the OTHER source on a playback error.
    const initialSource: Source =
      player === "cloudflare" && cloudflareId
        ? "cloudflare"
        : youtubeId
        ? "youtube"
        : cloudflareId
        ? "cloudflare"
        : "youtube";
    const [source, setSource] = useState<Source>(initialSource);

    const url =
      source === "cloudflare" && cloudflareId
        ? cloudflareHlsUrl(cloudflareId)
        : `youtube.com/watch?v=${youtubeId}&start=${startTime / 1000}`;

    const playerRef = useRef<ReactPlayer>(null);
    const [playing, setPlaying] = useState(false);
    // The underlying YouTube/HLS player only accepts play/seek calls once it has
    // finished initializing. Fill.tsx forces play() on the same tick the player
    // mounts (staged chat reveal), so without this gate ReactPlayer calls
    // `playVideo` before the YT iframe API exists → "method was not available".
    const [ready, setReady] = useState(false);

    // A fallback between YouTube↔Cloudflare swaps the underlying player, so the
    // new one starts un-ready — re-gate until its onReady fires.
    useEffect(() => {
      setReady(false);
    }, [source]);

    // One-shot fallback guard: without it, when both IDs exist and both fail,
    // handleError flips youtube<->cloudflare forever (infinite remount loop)
    // instead of giving up (same guard as feed/features/VideoPlayer.tsx).
    const fallbackAttemptedRef = useRef(false);

    // YouTube↔Cloudflare fallback on a playback error (iOS VideoPlayer parity).
    const handleError = () => {
      if (fallbackAttemptedRef.current) return;
      if (source === "youtube" && cloudflareId) {
        fallbackAttemptedRef.current = true;
        setSource("cloudflare");
        return;
      }
      if (source === "cloudflare" && youtubeId) {
        fallbackAttemptedRef.current = true;
        setSource("youtube");
      }
    };

    useImperativeHandle(ref, () => ({
      seekTo(time) {
        playerRef.current?.seekTo(time / 1000, "seconds");
      },
      pause() {
        handlePause();
      },
      play() {
        handlePlay();
      },
      triggerOverlayClick() {
        // Try to click the actual play button DOM element for better iOS Safari compatibility
        const playButton = document.querySelector(
          ".absolute.rounded-full.z-20"
        ) as HTMLElement;
        if (playButton) {
          playButton.click();
        } else {
          // Try clicking the ReactPlayer iframe directly
          const iframe = document.querySelector(
            'iframe[src*="youtube"]'
          ) as HTMLElement;
          if (iframe) {
            iframe.click();
          } else {
            // Fallback to the handler method
            handleOverlayClick();
          }
        }
      },
    }));

    const handlePause = useCallback(() => {
      if (playing !== false) {
        setPlaying(false);
      }
    }, [playing]);

    const handlePlay = () => {
      if (playing !== true) {
        setPlaying(true);
        onPlay();
      }
    };

    // tracks the watch time every X seconds
    // no big deal if this requests fails for whatever reason
    const trackWatchTime = (startTimeMs: number) => {
      if (!onTrackListeningTime) return;

      let lastMeasuredTimeSeconds = startTimeMs / 1000;
      const intervalTimeSeconds = 2;
      const trackWatchTime = setInterval(() => {
        // kill captions from the youtube video (YouTube source only)
        if (source === "youtube") {
          try {
            // react-player exposes no API to disable captions, so reach through
            // its wrapper chain to the underlying YT.Player and unload the
            // caption modules directly. Wrapped in try/catch since this relies
            // on react-player's internal shape and may break on upgrades.
            const ytInternals = playerRef.current as unknown as {
              player: {
                player: {
                  player: { unloadModule: (moduleName: string) => void };
                };
              };
            };
            ytInternals.player.player.player.unloadModule("cc");
            ytInternals.player.player.player.unloadModule("captions");
          } catch (err) {
            console.warn(err);
          }
        }
        if (playerRef.current && playerRef.current.getCurrentTime) {
          const currentTimeSeconds = playerRef.current.getCurrentTime();
          // realistic amount of watch time passed, post it
          // gives us space for a valid time add on the high end
          const timeBuffer = 2;
          const timeDiff = currentTimeSeconds - lastMeasuredTimeSeconds;
          if (0 < timeDiff && timeDiff <= intervalTimeSeconds + timeBuffer) {
            const timeDiffFloored = Math.floor(timeDiff);
            onTrackListeningTime(timeDiffFloored);
            const localDate = new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD format
            const data: PostListenTimeRequest = {
              listenTime: timeDiffFloored,
              localDate,
            };
            callWithAuth(postListenTime, data).catch((err) => {
              console.error(err);
            });
          }
          lastMeasuredTimeSeconds = currentTimeSeconds;
        }
      }, intervalTimeSeconds * 1000);
      return trackWatchTime;
    };

    useEffect(() => {
      // Don't poll while paused — the time can't move, so it's pure battery burn.
      if (!ready || !playing) return;
      const interval = setInterval(() => {
        if (playerRef.current && playerRef.current.getCurrentTime) {
          const currentTime = playerRef.current.getCurrentTime() * 1000;
          if (currentTime >= endTime || currentTime < startTime) {
            playerRef.current.seekTo(startTime / 1000, "seconds");
          }
          onTimeUpdate(currentTime);
        }
      }, 100);
      return () => {
        clearInterval(interval);
      };
    }, [startTime, endTime, onTimeUpdate, playing, ready]);

    // tracking the watch time
    useEffect(() => {
      // trackWatchTime returns undefined when tracking is disabled — guard the
      // cleanup so we don't call clearInterval on an undefined handle.
      const watchTimeInterval = trackWatchTime(startTime);
      return () => {
        if (watchTimeInterval) clearInterval(watchTimeInterval);
      };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startTime, endTime, source]);

    // Add effect to pause video when upgrading
    useEffect(() => {
      if (isUpgrading) {
        handlePause();
      }
    }, [isUpgrading, handlePause]);

    const handleOverlayClick = () => {
      if (playing !== true) {
        setPlaying(true);
        onPlay();
      } else {
        setPlaying(false);
      }
    };

    return (
      <div className="relative justify-center w-full">
        <AspectRatio ratio={16 / 9} className="p-0" minHeight="200px">
          <div className="relative">
            <ReactPlayer
              ref={playerRef}
              url={url}
              // Only request playback once the player is ready — declarative
              // `playing` is applied in onReady, so a true value queued before
              // then is honored without calling playVideo on a dead player.
              playing={playing && ready}
              onReady={() => setReady(true)}
              onPlay={handlePlay}
              onPause={handlePause}
              onError={handleError}
              height="100%"
              width="100%"
              config={{
                youtube: {
                  playerVars: {
                    hl: "es", // YouTube player interface language to Spanish
                  },
                },
                // Pin a current hls.js + begin loading at the clip offset so
                // deep-offset Cloudflare clips don't stall for minutes (shared
                // helper; see feed VideoPlayer). Harmless for YouTube sources.
                file: cloudflareFileConfig(startTime),
              }}
            />
            {!playing && (
              <div
                onClick={handleOverlayClick}
                className={`absolute rounded-full z-20 bg-[#FAFAFA] bg-opacity-80 flex items-center justify-center w-16 h-16 -translate-x-1/2 -translate-y-1/2 left-1/2 top-1/2`}
              >
                <div className="absolute z-5 left-[53%] top-1/2 -translate-x-1/2 -translate-y-1/2">
                  <IoMdPlay color="black" size="2em" />
                </div>
              </div>
            )}
          </div>
        </AspectRatio>
        {!playing && (
          <div
            id="overlay-paused"
            onClick={handleOverlayClick}
            className="absolute inset-0 z-0 bg-black bg-opacity-20 flex items-center justify-center pointer-events-none"
          ></div>
        )}
      </div>
    );
  }
);

export default ActivityPlayer;
